import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Bell, CheckCheck, Megaphone, Clock } from "lucide-react";

const NotificationsPage = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${API}/notifications`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications);
        setUnreadCount(data.unread_count);
      }
    } catch {} finally { setLoading(false); }
  };

  const markRead = async (id) => {
    await fetch(`${API}/notifications/read/${id}`, { method: "POST", credentials: "include" });
    setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await fetch(`${API}/notifications/read-all`, { method: "POST", credentials: "include" });
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    toast.success("All notifications marked as read");
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-obsidian" data-testid="notifications-page">
      <Sidebar />
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold">
              NOTIF<span className="text-crimson">ICATIONS</span>
            </h1>
            {unreadCount > 0 && (
              <p className="text-gray-400 text-sm mt-1">{unreadCount} unread</p>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              onClick={markAllRead}
              variant="outline"
              className="border-[#333] text-white text-xs"
              data-testid="mark-all-read"
            >
              <CheckCheck className="w-4 h-4 mr-1" /> Mark All Read
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-[40vh]">
            <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notifications.length > 0 ? (
          <div className="space-y-2">
            {notifications.map((n, i) => (
              <motion.div
                key={n.notification_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className={`border p-4 cursor-pointer transition-colors ${
                  n.read
                    ? "bg-obsidian-light border-[#1a1a1a] opacity-70"
                    : "bg-obsidian-light border-crimson/30 hover:border-crimson/50"
                }`}
                onClick={() => !n.read && markRead(n.notification_id)}
                data-testid={`notification-${n.notification_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center shrink-0 ${
                    n.read ? "bg-[#1a1a1a]" : "bg-crimson/10"
                  }`}>
                    {n.type === "broadcast"
                      ? <Megaphone className={`w-4 h-4 ${n.read ? "text-gray-600" : "text-crimson"}`} />
                      : <Bell className={`w-4 h-4 ${n.read ? "text-gray-600" : "text-crimson"}`} />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className={`text-sm font-bold ${n.read ? "text-gray-500" : "text-white"}`}>{n.title}</h3>
                      {!n.read && <span className="w-2 h-2 bg-crimson rounded-full shrink-0" />}
                    </div>
                    <p className={`text-sm ${n.read ? "text-gray-600" : "text-gray-400"}`}>{n.message}</p>
                  </div>
                  <div className="flex items-center gap-1 text-gray-600 text-xs shrink-0">
                    <Clock className="w-3 h-3" />
                    {formatTime(n.created_at)}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="bg-obsidian-light border border-[#222] p-12 text-center">
            <Bell className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No notifications yet</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default NotificationsPage;
